import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import './App.css';
import {
    Button,
    Box,
    Container,
    Grid,
    IconButton,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from "@mui/icons-material/Send";
import axios from 'axios';
import AppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import MenuIcon from '@mui/icons-material/Menu';
import Menu from '@mui/material/Menu';
import Toolbar from '@mui/material/Toolbar';
import { Routes, Route, useNavigate, useLocation } from "react-router";
// Other utils
import { mockMessages } from './utils/globalCons';
import { createAppTheme, ThemeSwitcher } from './utils/themeUtils';
import { ConversationHistoryDialog, ChatMessages } from './utils/chatUtils';
import { EmailTranscriptDialog } from './utils/emailUtils';
import {
    ChatbotDropdown
} from './utils/chatbotDropdownUtils';
// Professor utils
import PromptEditor from './pages/PromptEditor';
import ProfessorView from './pages/ProfessorView';

// Routes that are only ever reachable by faculty/professor accounts.
// Keep this list in sync with the <Route> definitions inside the Routes below.
const PROFESSOR_ONLY_ROUTES = ['/profprompt', '/profview', '/proflookup'];
const isProfessorOnlyPath = (pathname) => PROFESSOR_ONLY_ROUTES.includes(pathname);

const FacultyRoute = ({ isFaculty, children }) => {
    if (!isFaculty) {
        return (
            <Container maxWidth="sm" sx={{ mt: 6 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Access denied</Typography>
                <Typography color="text.secondary">
                    This page is only available to faculty accounts.
                </Typography>
            </Container>
        );
    }

    return children;
};

// =============================================
// PLAY AUDIO FUNCTION
// =============================================
const playAudio = (audioUrl) => {
    if (audioUrl) {
        const audio = new Audio(audioUrl);
        audio.play().catch(error => {
            console.error('Error playing audio:', error);
        });
    }
};

// =============================================
// AUDIO CONTROLS COMPONENT
// =============================================
// IMPORTANT: This is defined at module scope (not inside App()) so it keeps
// a stable component identity across renders. When it used to be declared
// inside App(), any App-level state change (e.g. setAudioFile) caused React
// to treat it as a brand-new component type and remount it, silently
// resetting its in-progress recording state (this was the cause of the
// "have to click Start Recording twice" bug).
const AudioControls = ({
    recordedAudioUrl,
    setRecordedAudioUrl,
    setAudioFile,
    onRecordingStopped,
    isTranscribing,
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const mediaStreamRef = useRef(null);

    useEffect(() => {
        return () => {
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const startRecording = async () => {
        try {
            setAudioFile(null);
            setRecordedAudioUrl(null);
            mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(mediaStreamRef.current, {
                mimeType: 'audio/webm;codecs=opus'
            });
            audioChunksRef.current = [];
            mediaRecorderRef.current.start();
            setIsRecording(true);

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
                const audioFile = new File([audioBlob], "voice-message.webm", {
                    type: 'audio/webm;codecs=opus',
                    lastModified: Date.now(),
                });
                const audioUrl = URL.createObjectURL(audioBlob);
                setRecordedAudioUrl(audioUrl);
                setIsRecording(false);
                setAudioFile(audioFile);
                if (onRecordingStopped) {
                    onRecordingStopped(audioFile);
                }
            };
        } catch (e) {
            console.error("Error accessing microphone:", e);
            alert("Error accessing microphone: " + e.message);
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
    };

    const playRecording = () => {
        if (recordedAudioUrl) {
            const audio = new Audio(recordedAudioUrl);
            audio.play().catch(error => {
                console.error('Error playing recording:', error);
            });
        }
    };

    return (
        <Container>
            <Box sx={{ width: "100%", mt: 4 }}>
                <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                    <Grid item xs="auto">
                        <IconButton
                            color="primary"
                            aria-label="start recording"
                            onClick={startRecording}
                            disabled={isRecording || isTranscribing}
                            sx={{
                                backgroundColor: 'background.paper',
                                boxShadow: 2,
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: 4,
                                },
                            }}
                        >
                            <MicIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={12} md>
                        <IconButton
                            color="secondary"
                            aria-label="stop recording"
                            onClick={stopRecording}
                            disabled={!isRecording}
                            sx={{
                                backgroundColor: 'background.paper',
                                boxShadow: 2,
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: 4,
                                },
                            }}
                        >
                            <FiberManualRecordIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs="auto">
                        <Button
                            variant="outlined"
                            disableElevation
                            onClick={playRecording}
                            disabled={isRecording || !recordedAudioUrl}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            Preview Recording
                        </Button>
                    </Grid>
                    {(isTranscribing || recordedAudioUrl) && (
                        <Grid item xs="auto">
                            <Typography variant="body2" color="text.secondary">
                                {isTranscribing
                                    ? 'Transcribing…'
                                    : 'Edit the transcribed text above, then press Send.'}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Container>
    );
};

// =============================================
// MAIN APP COMPONENT
// =============================================
function App(props) {
    const LANGUAGE_MAP = useMemo(() => ({
        English: 'en',
        Japanese: 'ja',
        Chinese: 'zh',
        Russian: 'ru',
        Arabic: 'ar',
        German: 'de',
        Vietnamese: 'vi',
        Korean: 'ko',
        Spanish: 'es',
        French: 'fr',
    }), []);

    // Theme state
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('appTheme') || 'light';
    });

    const [authState, setAuthState] = useState({
        checked: false,
        isAuthenticated: false,
        isFaculty: false,
        email: '',
        name: '',
    });

    const [message, setMessage] = useState("");
    const [audioFile, setAudioFile] = useState(null);
    const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [pendingAudioFilename, setPendingAudioFilename] = useState(null);
    const [selectedChatbot, setSelectedChatbot] = useState("");
    const [userToken, setUserToken] = useState(null);
    const [conversationLength, setConversationLength] = useState(0);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [chatbotConfigs, setChatbotConfigs] = useState({});
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);

    // const flaskHOST = process.env.REACT_APP_FLASK_HOST;
    //const flaskPORT = process.env.REACT_APP_FLASK_PORT;


    const location = useLocation();
    const navigate = useNavigate();
    const chatMessagesRef = useRef(null);
    const previousPathRef = useRef(location.pathname);

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailStatus, setEmailStatus] = useState('');


    const [messages, setMessages] = useState(mockMessages);
    const [language, setLanguage] = useState("");


    // Theme change handler
    const handleThemeChange = (newTheme) => {
        setCurrentTheme(newTheme);
        localStorage.setItem('appTheme', newTheme);
    };

    // Create theme based on current selection
    const theme = createAppTheme(currentTheme);

    const [selectedLanguage, setSelectedLanguage] = useState("");
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [chatbotSearchTerm, setChatbotSearchTerm] = useState("");

    const getUniqueLanguages = (configs) => {
        const languages = Object.values(configs).map(config => config.language);
        return [...new Set(languages)].sort();
    };

    const ensureAuthenticated = useCallback(async () => {
        try {
            // Browser-console trace for SSO auth state checks.
            console.info('[SSO] Checking session via /api/auth/me');
            const response = await axios.get('/api/auth/me');
            const isFaculty = !!response.data?.is_faculty;
            const ssoEmail = response.data?.email || '';
            const ssoName = response.data?.name || '';
            console.info('[SSO] Authenticated user found', {
                email: ssoEmail,
                is_faculty: isFaculty,
                roles: response.data?.roles || [],
            });
            setAuthState({ checked: true, isAuthenticated: true, isFaculty, email: ssoEmail, name: ssoName });
            return true;
        } catch (error) {
            if (error.response?.status === 401) {
                const next = `${window.location.pathname}${window.location.search}`;
                console.warn('[SSO] No active session; redirecting to /api/auth/login', { next });
                window.location.assign(`/api/auth/login?next=${encodeURIComponent(next)}`);
                return false;
            }

            console.error('[SSO] Error checking auth status via /api/auth/me:', error);
            setAuthState({ checked: true, isAuthenticated: false, isFaculty: false, email: '', name: '' });
            return false;
        }
    }, []);

    const handleLogout = useCallback(async () => {
        let redirectUrl = '/api/auth/login?next=/';
        try {
            const response = await axios.post('/api/auth/logout');
            if (response?.data?.redirect_url) {
                redirectUrl = response.data.redirect_url;
            }
        } catch (error) {
            console.error('[SSO] Error during logout request:', error);
        } finally {
            localStorage.removeItem('userToken');
            setUserToken(null);
            setAuthState({ checked: false, isAuthenticated: false, isFaculty: false, email: '', name: '' });
            window.location.assign(redirectUrl);
        }
    }, []);




    // =============================================
    // FETCH CHATBOT CONFIGURATIONS
    // =============================================
    const fetchChatbotConfigs = useCallback(async () => {
        try {
            setIsLoadingConfigs(true);
            const response = await axios.get(`/api/data`);

            console.log('Fetched chatbot configs:', response.data);

            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                setChatbotConfigs(response.data);
                const languages = getUniqueLanguages(response.data);
                setAvailableLanguages(languages);
            } else {
                console.error('Unexpected data format:', response.data);
                setChatbotConfigs({});
                setAvailableLanguages([]);
            }
        } catch (error) {
            console.error('Error fetching chatbot configs:', error);
            setChatbotConfigs({});
        } finally {
            setIsLoadingConfigs(false);
        }
    }, []);

    // =============================================
    // CONVERSATION MANAGEMENT FUNCTIONS
    // =============================================

    // Wrapped in useCallback to ensure a stable function reference
    const generateNewUserToken = useCallback(async () => {
        try {
            console.log('Requesting new user token from backend...');
            const response = await axios.post(`/api/start_conversation`);
            const newToken = response.data.user_token;
            return newToken;
        } catch (error) {
            console.error('Error generating new user token:', error);
            return null;
        }
    }, []); // Empty dependency array for stability

    // =============================================
    // COMPONENT INITIALIZATION EFFECT
    // =============================================
    useEffect(() => {
        let isMounted = true; // Flag to track if component is mounted

        const initializeApp = async () => {
            // Only proceed if the component is still mounted
            if (!isMounted) return;

            const authenticated = await ensureAuthenticated();
            if (!authenticated || !isMounted) {
                return;
            }

            // Call fetchChatbotConfigs here. Since it's memoized with useCallback,
            // React knows it's the same function reference, and it will only execute
            // its internal logic once per mount cycle (even in Strict Mode's double render).
            await fetchChatbotConfigs();

            // Check localStorage for an existing token first.
            // Only generate a new token if one truly doesn't exist.
            let storedToken = localStorage.getItem('userToken');
            if (!storedToken) {
                const newToken = await generateNewUserToken();
                if (isMounted && newToken) { // Check isMounted again before setting state
                    localStorage.setItem('userToken', newToken);
                    setUserToken(newToken);
                    console.log('App initialized with NEW token::', newToken);
                }
            } else {
                if (isMounted) { // Check isMounted before setting state
                    setUserToken(storedToken);
                    console.log('App initialized with EXISTING token::', storedToken);
                }
            }

            // Set the initial greeting message. This should happen regardless of token generation.
            if (isMounted) {
                setMessages(mockMessages);
            }
        };

        initializeApp();

        // Cleanup function: set isMounted to false when component unmounts
        return () => {
            isMounted = false;
        };
    }, [ensureAuthenticated, fetchChatbotConfigs, generateNewUserToken]);

    useEffect(() => {
        const wasPromptEditor = previousPathRef.current === '/profprompt';
        const isDashboard = location.pathname === '/';
        const isProfessorDashboard = isDashboard && authState.isFaculty && getParameterByName('prof') === 'true';

        if (wasPromptEditor && isProfessorDashboard) {
            fetchChatbotConfigs();
        }

        previousPathRef.current = location.pathname;
    }, [location.pathname, location.search, authState.isFaculty, fetchChatbotConfigs]);



    // =============================================
    // CLEAR CONVERSATION HISTORY FUNCTION
    // =============================================
    const clearConversationHistory = useCallback(async () => {
        try {
            if (userToken) {
                await axios.post(`/api/clear_conversation`, {
                    user_token: userToken
                });
                console.log(`Cleared history for token: ${userToken}`);
            }

            const newToken = await generateNewUserToken();
            if (newToken) {
                localStorage.setItem('userToken', newToken);
                setUserToken(newToken); // Update state with the new token
                setConversationLength(0); // Reset conversation length here for the new token
            }
            setMessages(mockMessages);
            setEmailStatus('');
        } catch (error) {
            console.error('Error clearing conversation:', error);
        }
    }, [userToken, generateNewUserToken, setMessages, setUserToken, setConversationLength, setEmailStatus]);

    const loadConversationHistory = async () => {
        try {
            //const response = await axios.get(`https://${flaskHOST}:${flaskPORT}/get_conversation?user_token=${userToken}`);
            const response = await axios.get(`/api/get_conversation?user_token=${userToken}`);
            setConversationHistory(response.data.conversations);
            setShowHistory(true);
        } catch (error) {
            console.error('Error loading conversation history:', error);
        }
    };

    // =============================================
    // TEXT-TO-SPEECH (TTS) FUNCTION
    // =============================================
    const generateTTSAudio = useCallback(async (text) => {
        try {
            //const response = await axios.post(`https://${flaskHOST}:${flaskPORT}/tts`, {
            // Fetch the selectedChatbot config from chatbotConfigs to get the chatbot voice and language for tts generation
            const config = chatbotConfigs[selectedChatbot] || undefined;
            const languageToUse = (config && config.language) || 'English'; // Default to English if no language found   
            const response = await axios.post(`/api/tts`, {
                text: text,
                voice: config.voice || 'alloy', // Default to alloy if no config found
                language: languageToUse
            });

            if (response.data && response.data.audio_filename) {
                const audioUrl = `/api/audio/${response.data.audio_filename}`;
                console.log("Generating text to speech");
                return audioUrl;
            }
            return null;
        } catch (error) {
            console.error('Error generating TTS:', error);
            return null;
        }
    }, [selectedChatbot, chatbotConfigs]);

    // =============================================
    // HANDLE CHATBOT CHANGE FUNCTION
    // =============================================
    const handleChatbotChange = useCallback(async (event) => {
        const chatbot = event.target.value;
        setSelectedChatbot(chatbot);

        if (!chatbot || !chatbotConfigs[chatbot]) {
            setMessages(mockMessages);
            return;
        }

        // When a new chatbot is selected, always generate a new token for the new conversation.
        const newToken = await generateNewUserToken();
        if (!newToken) {
            console.error("Failed to generate new token on chatbot change.");
            return;
        }
        setUserToken(newToken);
        localStorage.setItem('userToken', newToken);
        setConversationLength(0); // Reset conversation length here for the new token

        const config = chatbotConfigs[chatbot];
        setLanguage(config.language);
        localStorage.setItem("chatbotLanguage", config.language);

        const initialGreetingMessage = {
            role: 'assistant',
            content: config.initialText,
            text: config.initialText
        };
        setMessages([initialGreetingMessage]);

        // Add this line in for loading an audio on chatbot switch (costs more money)
        // const audioUrl = await generateTTSAudio(config.initialText);

        // Only update the initial greeting with audioUrl if it's still the current message
        setMessages(prevMessages => {
            if (prevMessages.length === 1 && prevMessages[0].content === config.initialText) {
                return [{
                    ...prevMessages[0]//, audioUrl: audioUrl
                }];
            }
            return prevMessages;
        });
        // Removed redundant generateNewUserToken() call here.
    }, [chatbotConfigs, generateNewUserToken, setMessages, setLanguage, setSelectedChatbot, setUserToken, setConversationLength]);

    const handleLanguageChange = (event) => {
        const language = event.target.value;
        setSelectedLanguage(language);
        setSelectedChatbot(""); // Reset chatbot selection when language changes
        setMessages(mockMessages);
        setChatbotSearchTerm(""); // Reset search term when language changes
    };

    const getFilteredChatbots = () => {
        if (!selectedLanguage) return {};

        return Object.fromEntries(
            Object.entries(chatbotConfigs).filter(([key, config]) =>
                config.language === selectedLanguage
            )
        );
    };

    // =============================================
    // AUDIO RECORDING COMPONENT
    // =============================================
    // =============================================
    // MESSAGE HANDLING FUNCTIONS
    // =============================================

    const handleSendMessage = useCallback(async () => {
        if (!message && !audioFile) return;
        if (!selectedChatbot) {
            setMessages(prevMessages => [...prevMessages, {
                role: 'assistant',
                content: 'Please select a chatbot first.',
                text: 'Please select a chatbot first.'
            }]);
            setMessage('');
            setAudioFile(null);
            setRecordedAudioUrl(null);
            setPendingAudioFilename(null);
            return;
        }

        let currentToken = userToken;
        // Only generate a new token if userToken is truly null (e.g., first message in a fresh session).
        if (!currentToken) {
            currentToken = await generateNewUserToken();
            if (!currentToken) {
                console.error("Failed to get a user token for sending message.");
                return;
            }
            setUserToken(currentToken);
            localStorage.setItem('userToken', currentToken);
        }

        // Capture before resetting state below, since a recorded+transcribed
        // message is submitted differently (via /api/whisper's pre-transcribed
        // commit path) so the audio clip stays attached to the saved turn.
        const audioFilenameToCommit = pendingAudioFilename;

        setMessages(prevMessages => [...prevMessages, {
            role: 'user',
            content: message || 'Audio message',
            text: message || 'Audio message',
            audioUrl: recordedAudioUrl
        }]);

        const config = chatbotConfigs[selectedChatbot];

        setMessage('');
        setAudioFile(null);
        setRecordedAudioUrl(null);
        setPendingAudioFilename(null);
        const languageToUse = config.language || "English";
        setLanguage(languageToUse);
        console.info('[Chat] Sending text request', {
            chatbot: selectedChatbot,
            language: languageToUse,
            fromRecording: !!audioFilenameToCommit,
        });

        try {
            let response;
            if (audioFilenameToCommit) {
                const formData = new FormData();
                formData.append("user_token", currentToken);
                formData.append("selectedChatbot", selectedChatbot);
                formData.append("language", languageToUse);
                formData.append("pre_transcribed_filename", audioFilenameToCommit);
                formData.append("final_text", message || '');

                const whisperResponse = await axios.post(`/api/whisper`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const result = whisperResponse.data && whisperResponse.data[0];
                if (!result) {
                    throw new Error("Unexpected response structure");
                }
                response = {
                    data: {
                        response: result.openai_response?.response,
                        conversation_length: result.conversation_length,
                        language_used: result.language_used,
                        model_used: result.model_used,
                        used_rag: result.used_rag,
                        transcript_indexed: result.transcript_indexed,
                        debug_prompt: result.debug_prompt,
                    }
                };
            } else {
                response = await axios.post(`/api/get_response`, {
                    message: message || 'Audio message',
                    selectedChatbot: selectedChatbot,
                    user_token: currentToken,
                    language: languageToUse,
                });
            }

            console.info('[Chat] Text response metadata', {
                chatbot: selectedChatbot,
                language: response.data?.language_used || languageToUse,
                model: response.data?.model_used || 'unknown',
                used_rag: !!response.data?.used_rag,
            });
            console.info('[TranscriptIndex] Email-to-conversation link status', {
                user_token: currentToken,
                transcript_indexed: !!response.data?.transcript_indexed,
            });
            if (response.data?.debug_prompt) {
                console.group('[Prompt Debug] /api/get_response');
                console.log('SYSTEM PROMPT', response.data.debug_prompt.system_prompt || '');
                console.log('USER MESSAGE', response.data.debug_prompt.user_message || '');
                console.log('HAS RAG', !!response.data.debug_prompt.has_rag);
                console.groupEnd();
            }

            if (response.data.conversation_length !== undefined) {
                setConversationLength(response.data.conversation_length);
            }

            const responseText = response.data.response;

            setMessages(prevMessages => [...prevMessages, {
                role: 'assistant',
                content: responseText,
                text: responseText,
            }]);
            if (chatMessagesRef.current && chatMessagesRef.current.scrollToBottom) {
                chatMessagesRef.current.scrollToBottom();
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const backendErrorMessage = error?.response?.data?.error || error?.response?.data?.reason || '';
            if (backendErrorMessage) {
                console.error('Backend /api/get_response error payload:', error.response.data);
            }
            if (error.message.includes('certificate') || error.message.includes('SSL')) {
                setMessages(prevMessages => [...prevMessages, {
                    role: 'assistant',
                    content: 'Connection error: SSL certificate issue.',
                    text: 'Connection error: SSL certificate issue.'
                }]);
            } else {
                setMessages(prevMessages => [...prevMessages, {
                    role: 'assistant',
                    content: `Error: ${backendErrorMessage || error.message || 'Unknown error'}`,
                    text: `Error: ${backendErrorMessage || error.message || 'Unknown error'}`
                }]);
            }
            if (chatMessagesRef.current && chatMessagesRef.current.scrollToBottom) {
                chatMessagesRef.current.scrollToBottom();
            }
        }
    }, [message, audioFile, selectedChatbot, userToken, chatbotConfigs, generateNewUserToken, setMessages, setMessage, setAudioFile, setRecordedAudioUrl, setConversationLength, setLanguage, language, recordedAudioUrl, chatMessagesRef, pendingAudioFilename, setPendingAudioFilename]);

    // =============================================
    // RECORDING STOPPED -> TRANSCRIBE-ONLY FUNCTION
    // =============================================
    // Called by AudioControls right after the student clicks Stop. Sends the
    // clip to Whisper for transcription ONLY (no LLM call, no history write)
    // and populates the message field so the student can review/correct the
    // text before sending it like any other text message.
    const handleRecordingStopped = useCallback(async (file) => {
        setIsTranscribing(true);
        setPendingAudioFilename(null);

        const config = chatbotConfigs[selectedChatbot];
        const languageToUse = config?.language || "English";
        const whisperLanguage = LANGUAGE_MAP[languageToUse] || 'en';

        const formData = new FormData();
        formData.append("audio", file);
        formData.append("language_code", whisperLanguage);

        try {
            const response = await axios.post(`/api/transcribe`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setMessage(response.data?.transcript || '');
            setPendingAudioFilename(response.data?.filename || null);
        } catch (error) {
            console.error('Error transcribing audio:', error);
            setMessages(prevMessages => [...prevMessages, {
                role: 'assistant',
                content: 'Sorry, we could not transcribe that recording. Please try again.',
                text: 'Sorry, we could not transcribe that recording. Please try again.'
            }]);
        } finally {
            setIsTranscribing(false);
        }
    }, [chatbotConfigs, selectedChatbot, LANGUAGE_MAP, setMessages, setMessage, setPendingAudioFilename]);

    // =============================================
    // SEND TRANSCRIPT FUNCTION
    // =============================================
    const handleSendTranscript = useCallback(async (studentName, studentEmail, professorName, professorEmail, extraNote) => {
        if (!userToken) {
            setEmailStatus('No active conversation to send.');
            return;
        }

        setEmailStatus('Sending transcript...');
        try {
            const response = await axios.post(`/api/send_transcript`, {
                user_token: userToken,
                professor_email: professorEmail,
                professor_name: professorName,
                extra_note: extraNote,
                student_email: studentEmail,
                student_name: studentName,
                chatbot_title: selectedChatbot
            });
            const data = response.data;

            if (response.status === 200) {
                setEmailStatus('Transcript sent successfully!');
            } else {
                setEmailStatus(`Failed to send transcript: ${data.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error sending transcript:', error);
            setEmailStatus(`Failed to send transcript: ${error.response?.data?.error || error.message}`);
        }
    }, [userToken, selectedChatbot, chatbotConfigs, setEmailStatus]);


    function getParameterByName(name, url = window.location.href) {
        name = name.replace(/[[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    const isProfessorMode = authState.isFaculty && getParameterByName('prof') === 'true';


    function returnStudentNavbar() {
        const isProfessorRoute = isProfessorOnlyPath(location.pathname);

        if (!isProfessorMode && !isProfessorRoute) {
            return (
                <AppBar position="fixed" sx={{
                    backgroundColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                    <Toolbar>
                        {authState.isFaculty && (
                            <Button color="inherit" onClick={() => navigate('/profview')} sx={{ mr: 1 }}>
                                Professor Dashboard
                            </Button>
                        )}
                        <Button color="inherit" onClick={handleLogout} sx={{ mr: 1 }}>
                            Logout
                        </Button>
                        <ThemeSwitcher currentTheme={currentTheme} onThemeChange={handleThemeChange} />
                    </Toolbar>
                </AppBar>
            );
        }
        return null;
    }

    function ProfessorNavbar() {
        const [anchorEl, setAnchorEl] = React.useState(null);
        const navigate = useNavigate();
        const location = useLocation(); // Get current route path

        const handleMenuOpen = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        const handleNavigation = (path) => {
            const leaveGuard = window.__promptEditorOcrLeaveGuard;
            if (typeof leaveGuard === 'function') {
                Promise.resolve(leaveGuard()).then((canLeave) => {
                    if (canLeave) {
                        navigate(path);
                    }
                    handleMenuClose();
                });
                return;
            }

            navigate(path);
            handleMenuClose();
        };

        const handleExternalNavigation = (url) => {
            const leaveGuard = window.__promptEditorOcrLeaveGuard;
            if (typeof leaveGuard === 'function') {
                Promise.resolve(leaveGuard()).then((canLeave) => {
                    if (canLeave) {
                        window.location.assign(url);
                    }
                    handleMenuClose();
                });
                return;
            }

            window.location.assign(url);
            handleMenuClose();
        };

        const handleProfessorLogout = () => {
            handleMenuClose();
            handleLogout();
        };

        // Check if we're in professor mode OR on professor routes
        const isProfessorMode = authState.isFaculty && getParameterByName('prof') === 'true';
        const isProfessorRoute = isProfessorOnlyPath(location.pathname);

        if ((!isProfessorMode && !isProfessorRoute) || !authState.isFaculty) {
            return null; // Don't render the navbar unless in professor context
        }

        return (
            <AppBar position="fixed" sx={{ backgroundColor: 'primary.main' }}>
                <Toolbar>
                    {/* Logo / Title */}
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{ flexGrow: 1, fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => {
                            const leaveGuard = window.__promptEditorOcrLeaveGuard;
                            if (typeof leaveGuard === 'function') {
                                Promise.resolve(leaveGuard()).then((canLeave) => {
                                    if (canLeave) {
                                            navigate('/profview');
                                    }
                                });
                                return;
                            }

                                navigate('/profview');
                        }}
                    >
                        Professor Dashboard
                    </Typography>

                    {/* Theme switcher next to title */}
                    <ThemeSwitcher currentTheme={currentTheme} onThemeChange={handleThemeChange} />


                    {/* Hamburger Icon */}
                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenuOpen}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Dropdown Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
                                mt: 1.5,
                                '& .MuiList-root': { py: 0 },
                                '& .MuiMenuItem-root': {
                                    px: 2,
                                    py: 1.2,
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                        color: 'primary.contrastText',
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem onClick={() => handleExternalNavigation("https://chatbot.dickinson.edu")}>
                            Chatbot Home
                        </MenuItem>
                        <MenuItem onClick={() => handleNavigation("/profprompt")}>
                            Prompts
                        </MenuItem>
                        <MenuItem onClick={() => handleNavigation("/profview")}>
                            Student Transcripts
                        </MenuItem>
                        <MenuItem onClick={() => handleNavigation("/proflookup")}>
                            Individual Student
                        </MenuItem>
                        <MenuItem onClick={handleProfessorLogout}>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                background: currentTheme === 'gradient' ?
                    'linear-gradient(135deg, #FFE0E6 0%, #FFE5E0 50%, #FFF0F5 100%)' :
                    'background.default',
                transition: 'all 0.3s ease',
            }}>
                <ProfessorNavbar />
                {returnStudentNavbar()}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        width: { xs: '100%', sm: (isProfessorMode || isProfessorOnlyPath(location.pathname)) ? `calc(100% - 140px)` : '100%' },
                        mt: (isProfessorMode || isProfessorOnlyPath(location.pathname)) ? 0 : 8,
                    }}
                >
                    {(isProfessorMode || isProfessorOnlyPath(location.pathname)) && <Toolbar />}
                    <Routes>
                        <Route path="/profprompt" element={
                            <FacultyRoute isFaculty={authState.isFaculty}>
                                <PromptEditor />
                            </FacultyRoute>
                        } />
                        <Route path="/profview" element={
                            <FacultyRoute isFaculty={authState.isFaculty}>
                                <ProfessorView themeMode={currentTheme} onThemeChange={handleThemeChange} initialView="students" />
                            </FacultyRoute>
                        } />
                        <Route path="/proflookup" element={
                            <FacultyRoute isFaculty={authState.isFaculty}>
                                <ProfessorView themeMode={currentTheme} onThemeChange={handleThemeChange} initialView="lookup" />
                            </FacultyRoute>
                        } />
                        <Route path="/" element={
                            <Container maxWidth="md" className="main-container" sx={{ px: { xs: 1, sm: 3 } }}> {/* Added responsive padding */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="h4" component="h1" sx={{
                                        textAlign: "center",
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        textShadow: currentTheme === 'dark' ? '0 2px 4px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
                                    }}>
                                        Voice Chatbot
                                    </Typography>
                                </Box>

                                {/* Language and Chatbot Selection display*/}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth className="language-select">
                                            <InputLabel id="language-select-label">Select Language</InputLabel>
                                            <Select
                                                labelId="language-select-label"
                                                id="language-select"
                                                value={selectedLanguage}
                                                label="Select Language"
                                                onChange={handleLanguageChange}
                                                disabled={isLoadingConfigs}
                                                sx={{
                                                    backgroundColor: 'background.paper',
                                                    borderRadius: 2,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.dark',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            >
                                                {isLoadingConfigs ? (
                                                    <MenuItem disabled>Loading languages...</MenuItem>
                                                ) : availableLanguages.length === 0 ? (
                                                    <MenuItem disabled>No languages available</MenuItem>
                                                ) : (
                                                    availableLanguages.map((language) => (
                                                        <MenuItem key={language} value={language}>
                                                            {language}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ChatbotDropdown
                                            value={selectedChatbot}
                                            onChange={handleChatbotChange}
                                            chatbotConfigs={chatbotConfigs}
                                            selectedLanguage={selectedLanguage}
                                            searchTerm={chatbotSearchTerm}
                                            setSearchTerm={setChatbotSearchTerm}
                                            disabled={isLoadingConfigs || !selectedLanguage}
                                            label="Select Chatbot"
                                        />
                                    </Grid>
                                </Grid>

                                <Box className="chat-window-wrapper">
                                    <ChatMessages
                                        ref={chatMessagesRef}
                                        messages={messages}
                                        playAudio={playAudio}
                                        generateTTSAudio={generateTTSAudio}
                                        setMessages={setMessages}
                                        selectedChatbot={selectedChatbot}
                                        chatbotConfigs={chatbotConfigs}
                                        className="chat-window"
                                    />
                                </Box>

                                <Box className="message-input">
                                    <Box sx={{ display: "flex", alignItems: "center", marginTop: 2, width: "100%" }}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={isTranscribing ? "Transcribing your recording…" : "Type your message"}
                                            value={message}
                                            onChange={(event) => setMessage(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" && !event.nativeEvent.isComposing && event.keyCode !== 229) {
                                                    event.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            inputProps={{
                                                autoComplete: 'off',
                                                autoCorrect: 'off',
                                                autoCapitalize: 'off',
                                                spellCheck: 'false'
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => {
                                                handleSendMessage();
                                            }}
                                            disabled={!message.trim() || isTranscribing}
                                            sx={{ marginLeft: 1 }}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box className="audio-controls">
                                    <AudioControls
                                        recordedAudioUrl={recordedAudioUrl}
                                        setRecordedAudioUrl={setRecordedAudioUrl}
                                        setAudioFile={setAudioFile}
                                        onRecordingStopped={handleRecordingStopped}
                                        isTranscribing={isTranscribing}
                                    />
                                </Box>


                                <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={clearConversationHistory}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1.5,
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    borderColor: 'primary.dark',
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                },
                                            }}
                                        >
                                            Clear History
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={loadConversationHistory}
                                            disabled={!userToken}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1.5,
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    borderColor: 'primary.dark',
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                },
                                            }}
                                        >
                                            View History ({conversationLength})
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => setShowEmailModal(true)}
                                            disabled={!userToken || conversationLength === 0}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1.5,
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    borderColor: 'primary.dark',
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                },
                                            }}
                                        >
                                            Email Transcript
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Please contact{' '}
                                        <a
                                            href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}
                                            style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                                        >
                                            {process.env.REACT_APP_SUPPORT_EMAIL}
                                        </a>{' '}
                                        for support.
                                    </Typography>
                                </Box>
                                <ConversationHistoryDialog
                                    showHistory={showHistory}
                                    setShowHistory={setShowHistory}
                                    conversationHistory={conversationHistory}
                                />

                                <EmailTranscriptDialog
                                    showEmailModal={showEmailModal}
                                    setShowEmailModal={setShowEmailModal}
                                    emailStatus={emailStatus}
                                    setEmailStatus={setEmailStatus}
                                    handleSendTranscript={handleSendTranscript}
                                    selectedChatbot={selectedChatbot}
                                    chatbotConfigs={chatbotConfigs}
                                    ssoStudentEmail={authState.email}
                                />

                            </Container>
                        } />
                    </Routes>
                </Box>

            </Box>
        </ThemeProvider>
    );
}

export default App;
